/** Whether an element has been on screen at least once — what a record page waits for before
 *  it asks for a lazy panel's rows: the activity record's "Where it's used" rows and used-by
 *  summary (plan §9.5, D7: the summary reads the tour-side tables, so it is never asked with
 *  the detail), the activity category record's "Activities in this category".
 *
 *  Attach the ref callback to the element. Once any part of it intersects the viewport the
 *  answer stays true (the observer is dropped). Where the browser has no
 *  `IntersectionObserver` the element counts as seen once it is mounted. */
import { useEffect, useState, type RefCallback } from 'react';

export function useSeenOnce<T extends Element>(): [RefCallback<T>, boolean] {
  const [el, setEl] = useState<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (seen || !el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setSeen(true);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [el, seen]);
  return [setEl, seen];
}
